import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, Button, Tag, Input, Select, Space, Card, Typography, Modal, Form, message,
  Popconfirm, Tooltip, InputNumber,
} from 'antd';
import { Plus, Search, Eye, Pencil, Trash2 } from 'lucide-react';
import { leadsApi } from '../../services/api';

const { Title } = Typography;

const statusColors: Record<string, string> = {
  NEW: 'blue', CONTACTED: 'cyan', QUALIFIED: 'green',
  NURTURING: 'orange', HOT: 'red', CONVERTED: 'purple', LOST: 'default',
};

const sourceLabels: Record<string, string> = {
  web: 'Web', referral: 'Referido', portal: 'Portal',
  walkin: 'Visita', phone: 'Teléfono', social: 'Redes sociales',
  WEB: 'Web (genérico)',
  WEB_CONTACT: 'Contacto web',
  WEB_COMPRADOR: 'Comprador web',
  valorador: 'Valorador online',
  'canal-denuncias': 'Canal de denuncias',
  'trabaja-con-nosotros': 'Trabaja con nosotros',
  'colabora-particular': 'Colabora como particular',
  'personal-shopper': 'Personal Shopper',
  'publicacion-portales': 'Publicación en portales',
  'unete-asociado': 'Únete — Asociado',
  'unete-prive': 'Únete — Privé',
  'unete-referencia': 'Únete — Referencia',
};

export default function LeadsPage() {
  const [leads, setLeads] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState<any>({});
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string | undefined>();
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const fetchLeads = () => {
    setLoading(true);
    leadsApi.list({ page, limit: 20, search: search || undefined, status })
      .then((r) => { setLeads(r.data.data); setMeta(r.data.meta); })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchLeads(); }, [page, status]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (record: any) => {
    setEditing(record);
    form.setFieldsValue({
      firstName: record.firstName,
      lastName: record.lastName,
      email: record.email,
      phone: record.phone,
      source: record.source,
      status: record.status,
      budget: record.budget,
      preferredZone: record.preferredZone,
      propertyType: record.propertyType,
      bedrooms: record.bedrooms,
      notes: record.notes,
    });
    setModalOpen(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      if (values.budget) values.budget = Number(values.budget);
      if (editing) {
        await leadsApi.update(editing.id, values);
        message.success('Lead actualizado');
      } else {
        await leadsApi.create(values);
        message.success('Lead creado');
      }
      setModalOpen(false);
      form.resetFields();
      setEditing(null);
      fetchLeads();
    } catch {
      message.error(editing ? 'Error al actualizar' : 'Error al crear');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await leadsApi.delete(id);
      message.success('Lead eliminado');
      fetchLeads();
    } catch {
      message.error('Error al eliminar');
    }
  };

  const columns = [
    {
      title: 'Nombre', key: 'name',
      render: (_: any, r: any) => `${r.firstName} ${r.lastName || ''}`,
    },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Teléfono', dataIndex: 'phone', key: 'phone' },
    {
      title: 'Origen', dataIndex: 'source', key: 'source',
      render: (s: string) => sourceLabels[s] || s,
    },
    {
      title: 'Estado', dataIndex: 'status', key: 'status',
      render: (s: string) => <Tag color={statusColors[s]}>{s}</Tag>,
    },
    {
      title: 'Etiquetas', dataIndex: 'tags', key: 'tags',
      render: (tags: string) => tags
        ? tags.split(',').map((t) => <Tag key={t} color="geekblue" style={{ marginBottom: 2 }}>{t.trim()}</Tag>)
        : '—',
    },
    {
      title: 'Score', dataIndex: 'score', key: 'score', width: 80,
      render: (s: number) => s != null ? (
        <Tag color={s >= 80 ? 'red' : s >= 50 ? 'orange' : s >= 25 ? 'blue' : 'default'}>{s}</Tag>
      ) : '—',
    },
    {
      title: 'Presupuesto', dataIndex: 'budget', key: 'budget',
      render: (b: number) => b ? `€${b.toLocaleString()}` : '—',
    },
    {
      title: 'Zona', dataIndex: 'preferredZone', key: 'zone',
      render: (z: string) => z || '—',
    },
    {
      title: 'Acciones', key: 'actions', width: 150,
      render: (_: any, r: any) => (
        <Space size={4}>
          <Tooltip title="Ver"><Button type="link" size="small" icon={<Eye size={14} />} onClick={() => navigate(`/leads/${r.id}`)} /></Tooltip>
          <Tooltip title="Editar"><Button type="link" size="small" icon={<Pencil size={14} />} onClick={() => openEdit(r)} /></Tooltip>
          <Popconfirm title="¿Eliminar este lead?" description="Esta acción no se puede deshacer" onConfirm={() => handleDelete(r.id)} okText="Eliminar" cancelText="Cancelar" okButtonProps={{ danger: true }}>
            <Tooltip title="Eliminar"><Button type="link" size="small" danger icon={<Trash2 size={14} />} /></Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Leads</Title>
        <Button type="primary" icon={<Plus size={14} />} onClick={openCreate}>Nuevo Lead</Button>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input placeholder="Buscar..." prefix={<Search size={14} />} value={search} onChange={(e) => setSearch(e.target.value)} onPressEnter={fetchLeads} style={{ width: 250 }} />
          <Select placeholder="Estado" allowClear style={{ width: 160 }} value={status} onChange={setStatus} options={Object.entries(statusColors).map(([k]) => ({ value: k, label: k }))} />
        </Space>
      </Card>

      <Card>
        <Table columns={columns} dataSource={leads} loading={loading} rowKey="id" size="small"
          pagination={{ current: page, pageSize: 20, total: meta.total, onChange: setPage, showTotal: (t) => `${t} leads` }}
        />
      </Card>

      <Modal title={editing ? 'Editar Lead' : 'Nuevo Lead'} open={modalOpen} onCancel={() => { setModalOpen(false); setEditing(null); }} onOk={() => form.submit()} okText={editing ? 'Guardar' : 'Crear'} width={600} destroyOnClose>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item name="firstName" label="Nombre" rules={[{ required: true }]}><Input /></Form.Item>
            <Form.Item name="lastName" label="Apellido"><Input /></Form.Item>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item name="email" label="Email"><Input type="email" /></Form.Item>
            <Form.Item name="phone" label="Teléfono"><Input /></Form.Item>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item name="source" label="Origen" initialValue="web">
              <Select options={Object.entries(sourceLabels).map(([k, v]) => ({ value: k, label: v }))} />
            </Form.Item>
            {editing && (
              <Form.Item name="status" label="Estado">
                <Select options={Object.entries(statusColors).map(([k]) => ({ value: k, label: k }))} />
              </Form.Item>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item name="budget" label="Presupuesto (€)"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item>
            <Form.Item name="preferredZone" label="Zona preferida"><Input /></Form.Item>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item name="propertyType" label="Tipo propiedad">
              <Select allowClear options={['villa', 'apartment', 'finca', 'penthouse', 'commercial'].map((t) => ({ value: t, label: t.charAt(0).toUpperCase() + t.slice(1) }))} />
            </Form.Item>
            <Form.Item name="bedrooms" label="Habitaciones"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item>
          </div>
          <Form.Item name="score" label="Score (0-100)">
            <InputNumber style={{ width: '100%' }} min={0} max={100} placeholder="Puntuación del lead" />
          </Form.Item>
          <Form.Item name="notes" label="Notas"><Input.TextArea rows={3} /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
